import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

/**
 * Every key here is a deliberate deviation from stock Aura, and this object is the only place a
 * global one may live. Aura owns the semantic tier: the token NAME our CSS asks for is always
 * Aura's, and design reads the same tree in the PrimeNG Figma UI Kit. What a name points AT may be
 * our own primitive from variables.scss, set once, in one direction.
 *
 * A value may be a plain CSS string, so `var(--blue-700)` emits as `--p-primary-500: var(--blue-700)`.
 * Keys are not type-checked — `definePreset` takes Record<string, unknown> — so a typo paints nothing
 * silently. `scripts/primeng-preset-check.mjs` is what catches that; run it after editing this file.
 *
 * Scoped, opt-in deviations are NOT preset keys: they are classes in 3_modules/primeng-variants.scss.
 * Policy: docs/ui-migration-and-review/standards/aura-tokens.md
 */
export const SPROCKET_DEVIATIONS = {
    semantic: {
        // Stock Aura's primary is emerald. Lifted verbatim from 1_base/primeng-primary-ramp.scss,
        // which this replaces; 300→blue-400 and 400→blue-600 skip a stop on purpose.
        primary: {
            50: 'var(--blue-50)',
            100: 'var(--blue-100)',
            200: 'var(--blue-200)',
            300: 'var(--blue-400)',
            400: 'var(--blue-600)',
            500: 'var(--blue-700)',
            600: 'var(--blue-800)',
            700: 'var(--blue-900)',
            800: 'var(--blue-950)',
            900: 'var(--blue-1000)',
            950: 'var(--blue-1500)',
        },
        formField: {
            // 6px, the same number as Aura's {border.radius.md} — kept as one source so fields follow
            // --base-radius and the rest of Aura's radii do not.
            borderRadius: 'var(--base-radius)',
        },
        colorScheme: {
            // Colours are declared per scheme in Aura; the same key at semantic.formField would emit a token nothing reads.
            light: {
                formField: {
                    borderColor: 'var(--border-color)',       // grey-400. Aura: {surface.300}. Reaches 29 component tokens
                    hoverBorderColor: 'var(--grey-600)',      // Aura: {surface.400}
                    placeholderColor: 'var(--grey-700)',      // today's native input::placeholder. Aura: {surface.500}
                    invalidBorderColor: 'var(--red-700)',     // Aura's {red.400} is 2.77:1 on white, under 1.4.11's 3:1
                },
                content: {
                    borderColor: 'var(--border-color)',       // grey-400. Aura: {surface.200}. Inherited by menu, datatable, tabs and every other content border
                },
            },
        },
    },
    components: {
        menu: {
            submenuLabel: {
                //fontWeight: '500',                    // Aura: 600
                color: 'var(--base-text)',            // Aura: {text.muted.color}
                padding: '5.2px 0.75rem 1.2px 0.75rem;', // aura: 0.5rem 0.75rem
            },
            item: {
                focusBackground: 'var(--blue-1000)',  // Aura: {surface.100}
                focusColor: 'var(--color-white)',     // Aura: {text.hover.color}
                icon: {
                    focusColor: 'var(--grey-500)',    // Aura: {surface.500}
                },
            },
        },
        datatable: {
            headerCell: {
                // Only the keys read by .p-datatable-column-sorted / .p-datatable-sortable-column belong
                // here. padding/background/borderColor/color are read ONLY by .p-datatable-header-cell,
                // which PrimeNG's Angular table never applies, so they live in primeng-variants.scss.
                selectedBackground: 'transparent',    // Aura: {highlight.background}
                selectedColor: 'var(--blue-950)',     // Aura: {highlight.color}
            },
            bodyCell: {
                padding: '0.5rem',                    // Aura: 0.75rem 1rem
            },
            sortIcon: {
                color: 'var(--base-text)',            // Aura: {text.muted.color} = surface.500, a lighter caret
            },
        },
        select: {
            option: {
                padding: '.45rem .5rem',              // Aura: 0.5rem 0.75rem
            },
        },
        multiselect: {
            option: {
                padding: '.45rem .5rem',              // Aura: 0.5rem 0.75rem. Keep in step with select above
            },
        },
        tabs: {
            tabpanel: {
                background: 'transparent',            // Aura: {content.background}
            },
            tab: {
                padding: '.875rem 1rem',              // Aura: 1rem 1.125rem
                fontWeight: '500',                    // Aura: 600
            },
            activeBar: {
                height: '2px',                        // Aura: 1px
            },
        },
        toast: {
            root: {
                width: 'min(25rem, calc(100vw - 2rem))',  // Aura: 25rem, which overhangs phones
            },
            colorScheme: {
                light: {
                    // Fill and border match app-inline-alert per intent; summary text one ramp step darker to clear 4.5:1.
                    error: {
                        background: 'var(--red-100)',
                        borderColor: 'var(--red-800)',
                        color: 'var(--red-700)',
                        detailColor: 'var(--base-text)',
                        closeButton: { hoverBackground: 'var(--red-200)', focusRing: { color: 'var(--red-700)' } },
                    },
                    warn: {
                        background: 'var(--yellow-50)',
                        borderColor: 'var(--yellow-500)',
                        color: 'var(--yellow-800)',
                        detailColor: 'var(--base-text)',
                        closeButton: { hoverBackground: 'var(--yellow-200)', focusRing: { color: 'var(--yellow-800)' } },
                    },
                    success: {
                        background: 'var(--green-50)',
                        borderColor: 'var(--success)',
                        color: 'var(--green-800)',
                        detailColor: 'var(--base-text)',
                        closeButton: { hoverBackground: 'var(--green-200)', focusRing: { color: 'var(--green-800)' } },
                    },
                    info: {
                        background: 'var(--blue-50)',
                        borderColor: 'var(--blue-900)',
                        color: 'var(--blue-800)',
                        detailColor: 'var(--base-text)',
                        closeButton: { hoverBackground: 'var(--blue-200)', focusRing: { color: 'var(--blue-800)' } },
                    },
                },
            },
        },
        message: {
            text: {
                // Not var(--text-default): preset tokens resolve at :root (16px), and every wrapper already sets font-size to it.
                fontSize: '1em',  // Aura: 1rem
            },
            colorScheme: {
                light: {
                    // Same per-intent palette as toast above; keep the two in step.
                    error: {
                        background: 'var(--red-50)',
                        borderColor: 'var(--red-100)',
                        color: 'var(--red-700)',
                        outlined: { color: 'var(--red-700)', borderColor: 'var(--red-700)' },
                        simple: { color: 'var(--red-700)' },
                        closeButton: { hoverBackground: 'var(--red-200)', focusRing: { color: 'var(--red-700)' } },
                    },
                    warn: {
                        background: 'var(--yellow-25)',
                        borderColor: 'var(--yellow-500)',
                        color: 'var(--yellow-800)',
                        outlined: { color: 'var(--yellow-800)', borderColor: 'var(--yellow-800)' },
                        simple: { color: 'var(--yellow-800)' },
                        closeButton: { hoverBackground: 'var(--yellow-200)', focusRing: { color: 'var(--yellow-800)' } },
                    },
                    success: {
                        background: 'var(--green-50)',
                        borderColor: 'var(--green-300)',
                        color: 'var(--green-800)',
                        outlined: { color: 'var(--green-800)', borderColor: 'var(--green-800)' },
                        simple: { color: 'var(--green-800)' },
                        closeButton: { hoverBackground: 'var(--green-200)', focusRing: { color: 'var(--green-800)' } },
                    },
                    info: {
                        background: 'var(--blue-50)',
                        borderColor: 'var(--blue-100)',
                        color: 'var(--blue-800)',
                        outlined: { color: 'var(--blue-800)', borderColor: 'var(--blue-800)' },
                        simple: { color: 'var(--blue-800)' },
                        closeButton: { hoverBackground: 'var(--blue-200)', focusRing: { color: 'var(--blue-800)' } },
                    },
                },
            },
        },
    },
};

export const SprocketAura = definePreset(Aura, SPROCKET_DEVIATIONS);
